import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, BarChart3, Download } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { verifyIsAdmin } from "@/lib/admin.functions";
import {
  getCrossLeagueReport,
  type CrossLeagueReport,
} from "@/lib/cross-league-report.functions";
import {
  getKnockoutReport,
  type KnockoutReport,
} from "@/lib/knockout-report.functions";
import { renderCrossLeagueReportPdf } from "@/lib/print-cross-league-pdf.functions";
import { renderKnockoutReportPdf } from "@/lib/print-knockout-pdf.functions";
import { openPdfFromBase64 } from "@/lib/open-pdf";
import { CrossLeagueReportView } from "@/components/CrossLeagueReportView";
import { KnockoutReportView } from "@/components/KnockoutReportView";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/cross-league-report")({
  component: CrossLeagueReportPage,
  head: () => ({ meta: [{ title: "Cross-league insights — Bracket Goblin" }] }),
});

const TOURNAMENT_TYPE = "fifa_wc_mens_group_knockout" as const;

type KnockoutRound = "r32" | "r16" | "qf" | "sf" | "final" | "third_place";
type TabKey = "groups" | "r32" | "r16" | "qf" | "sf" | "final";

const ROUND_LABEL: Record<KnockoutRound, string> = {
  r32: "Round of 32",
  r16: "Round of 16",
  qf: "Quarterfinals",
  sf: "Semifinals",
  final: "Final",
  third_place: "3rd-place playoff",
};

function CrossLeagueReportPage() {
  const { session, loading } = useAuth();
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean | null>(null);
  const [groupReport, setGroupReport] = useState<CrossLeagueReport | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<KnockoutRound | "groups" | null>(null);
  const [tab, setTab] = useState<TabKey>("groups");

  const [roundReports, setRoundReports] = useState<
    Partial<Record<KnockoutRound, KnockoutReport>>
  >({});
  const [roundLoading, setRoundLoading] = useState<
    Partial<Record<KnockoutRound, boolean>>
  >({});

  const verifyAdminFn = useAuthedServerFn(verifyIsAdmin);
  const getReportFn = useAuthedServerFn(getCrossLeagueReport);
  const getKnockoutFn = useAuthedServerFn(getKnockoutReport);
  const renderPdfFn = useAuthedServerFn(renderCrossLeagueReportPdf);
  const renderKnockoutPdfFn = useAuthedServerFn(renderKnockoutReportPdf);

  useEffect(() => {
    if (!session) {
      setServerIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyAdminFn();
        if (cancelled) return;
        setServerIsAdmin(res.isAdmin);
        if (res.isAdmin) {
          const r = await getReportFn({
            data: { tournamentType: TOURNAMENT_TYPE },
          });
          if (!cancelled) setGroupReport(r);
        }
      } catch (e) {
        if (!cancelled) {
          setServerIsAdmin(false);
          setReportError(e instanceof Error ? e.message : "Failed to load");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const loadRound = (round: KnockoutRound) => {
    if (roundReports[round] || roundLoading[round] || !serverIsAdmin) return;
    setRoundLoading((s) => ({ ...s, [round]: true }));
    (async () => {
      try {
        const r = await getKnockoutFn({
          data: { tournamentType: TOURNAMENT_TYPE, round },
        });
        setRoundReports((s) => ({ ...s, [round]: r }));
      } catch (e) {
        toast.error(
          e instanceof Error
            ? e.message
            : `Failed to load ${ROUND_LABEL[round]} report`,
        );
      } finally {
        setRoundLoading((s) => ({ ...s, [round]: false }));
      }
    })();
  };

  // Lazy-load whichever round(s) the active tab needs
  useEffect(() => {
    if (!serverIsAdmin) return;
    if (tab === "groups") return;
    if (tab === "final") {
      loadRound("final");
      loadRound("third_place");
    } else {
      loadRound(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, serverIsAdmin]);

  const downloadGroupsPdf = async () => {
    setPdfLoading("groups");
    try {
      const res = await renderPdfFn({
        data: { tournamentType: TOURNAMENT_TYPE },
      });
      openPdfFromBase64(res.pdfBase64, res.filename);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "PDF failed");
    } finally {
      setPdfLoading(null);
    }
  };

  const downloadRoundPdf = async (round: KnockoutRound) => {
    setPdfLoading(round);
    try {
      const res = await renderKnockoutPdfFn({
        data: { tournamentType: TOURNAMENT_TYPE, round },
      });
      openPdfFromBase64(res.pdfBase64, res.filename);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "PDF failed");
    } finally {
      setPdfLoading(null);
    }
  };

  if (loading || (session && serverIsAdmin === null)) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }
  if (!serverIsAdmin) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground mb-2">
          Admins only
        </h1>
        <Button asChild variant="outline">
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </Button>
      </main>
    );
  }

  const renderRoundTab = (round: KnockoutRound) => {
    const report = roundReports[round];
    const isLoading = roundLoading[round];
    return (
      <div>
        <div className="flex justify-end mb-3">
          <Button
            onClick={() => downloadRoundPdf(round)}
            disabled={!report || pdfLoading === round}
            variant="outline"
            size="sm"
          >
            <Download className="w-4 h-4" />{" "}
            {pdfLoading === round ? "Rendering…" : "Download PDF"}
          </Button>
        </div>
        {isLoading && !report && (
          <div className="p-12 text-center text-muted-foreground">
            Crunching the numbers…
          </div>
        )}
        {report && <KnockoutReportView report={report} />}
      </div>
    );
  };

  return (
    <main className="max-w-6xl mx-auto px-4 py-8">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <Link
            to="/admin"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Back to admin
          </Link>
          <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Cross-league insights
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Picks aggregated across every league for the current World Cup. Super admin only.
          </p>
        </div>
        {tab === "groups" && groupReport && (
          <Button
            onClick={downloadGroupsPdf}
            disabled={pdfLoading === "groups"}
            variant="outline"
            size="sm"
          >
            <Download className="w-4 h-4" />{" "}
            {pdfLoading === "groups" ? "Rendering…" : "Download PDF"}
          </Button>
        )}
      </header>

      {reportError && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive p-4 text-sm mb-4">
          {reportError}
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList className="mb-4 flex flex-wrap h-auto">
          <TabsTrigger value="groups">Group stage</TabsTrigger>
          <TabsTrigger value="r32">Round of 32</TabsTrigger>
          <TabsTrigger value="r16">Round of 16</TabsTrigger>
          <TabsTrigger value="qf">Quarterfinals</TabsTrigger>
          <TabsTrigger value="sf">Semifinals</TabsTrigger>
          <TabsTrigger value="final">Final & 3rd</TabsTrigger>
        </TabsList>

        <TabsContent value="groups">
          {!groupReport && !reportError && (
            <div className="p-12 text-center text-muted-foreground">Crunching the numbers…</div>
          )}
          {groupReport && <CrossLeagueReportView report={groupReport} />}
        </TabsContent>

        <TabsContent value="r32">{renderRoundTab("r32")}</TabsContent>
        <TabsContent value="r16">{renderRoundTab("r16")}</TabsContent>
        <TabsContent value="qf">{renderRoundTab("qf")}</TabsContent>
        <TabsContent value="sf">{renderRoundTab("sf")}</TabsContent>

        <TabsContent value="final">
          <div className="space-y-8">
            <section>
              <h2 className="font-display text-xl font-bold text-foreground mb-3">
                Final
              </h2>
              {renderRoundTab("final")}
            </section>
            <section>
              <h2 className="font-display text-xl font-bold text-foreground mb-3">
                3rd-place playoff
              </h2>
              {renderRoundTab("third_place")}
            </section>
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}
